import { FC, Suspense, useEffect, useMemo, useState } from 'react';
import { useLazyLoadQuery } from 'react-relay';
import { exitSlipsBySkills } from '../../graphQLOperations/ExitSlip';
import { ExitSlipExitSlipsBySkillsQuery } from '../../graphQLOperations/__generated__/ExitSlipExitSlipsBySkillsQuery.graphql';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { selectUser } from '../../redux/reducers/userSlice';
import { selectedTargetGroup } from '../../redux/reducers/targetGroupSlice';
import { setExitSlipSkillProgress } from '../../redux/reducers/exitSlipSkillProgressSlice';
import { CustomBranding, mapCustomBranding } from '../../models/User';
import { readPendingExitSlipFinish } from '../../businessLogicService/StudentDashboardAux/exitSlipOutcome';
import { buildSkillExitSlipProgress } from '../../businessLogicService/StudentDashboardAux/skillExitSlipReport';

interface InnerProps {
  studentId: string;
  targetGroupId: string;
}

const SkillExitSlipReport: FC<InnerProps> = ({ studentId, targetGroupId }) => {
  const dispatch = useAppDispatch();
  const userId = useAppSelector(selectUser)?.id;

  const { studentExitSlipReport } = useLazyLoadQuery<ExitSlipExitSlipsBySkillsQuery>(exitSlipsBySkills, {
    studentId,
    targetGroupId,
  });

  // Read straight from storage rather than from exitSlipPendingFinishSlice: that slice is owned by
  // DashboardExitSlipQuery, which is not mounted on this page, so it is empty here on a fresh load.
  // localStorage is the durable half of the same record (writePendingExitSlipFinish is called the
  // moment the ticket is finished), and the student cannot sit a ticket from this page — so reading it
  // once on mount is enough.
  const [pendingFinish] = useState(() => readPendingExitSlipFinish(userId));

  const skillProgress = useMemo(
    () => buildSkillExitSlipProgress(studentExitSlipReport, pendingFinish),
    [studentExitSlipReport, pendingFinish],
  );

  useEffect(() => {
    dispatch(setExitSlipSkillProgress(skillProgress));
  }, [dispatch, skillProgress]);

  return null;
};

/**
 * Puts this student's exit-ticket report in redux for the skill page to read, the same report and the
 * same slice the dashboard's unit-path cards are built from (see DashboardSkillExitSlipReportQuery).
 *
 * Mounted here rather than relying on what the dashboard left behind, because the page has to stand on
 * its own: the exit-ticket result card links straight into /skill/:skillCode, and a reload or a shared
 * link arrives with an empty store. Sharing the slice is what keeps the two surfaces agreeing — the
 * chip, the fire and the "N/M" all come out of one record, scored by one rule.
 *
 * Only the skill progress is published. The other reading of this report — which ticket is the last one
 * standing, exitSlipSkillEarningSlice — belongs to today's banner on the dashboard and would be
 * answering a question nothing on this page asks.
 *
 * Scoped to the student's whole target group because that is the resolver's shape, not because the page
 * needs the other skills; the slice is keyed by skill id and this page looks up exactly one.
 */
export const SkillExitSlipReportQuery: FC = () => {
  const userRedux = useAppSelector(selectUser);
  const targetGroupRedux = useAppSelector(selectedTargetGroup);

  const isMiami =
    !!userRedux?.network?.customBranding &&
    mapCustomBranding[userRedux.network.customBranding] === CustomBranding.MIAMI;

  if (!isMiami || !userRedux?.id || !targetGroupRedux?.id) {
    return null;
  }

  // Its own boundary, with no fallback: this query renders nothing, so there is nothing to show while
  // it is in flight — and without the boundary it would suspend inside the page's own, holding the
  // whole skill page back on a request that only decorates its header. The chip and the message
  // appear when the report lands.
  return (
    <Suspense fallback={null}>
      <SkillExitSlipReport studentId={`StudentNode:${userRedux.id}`} targetGroupId={targetGroupRedux.id} />
    </Suspense>
  );
};

export default SkillExitSlipReportQuery;
